import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@material-ui/core';
import { Close, Forum, Person } from '@material-ui/icons';
import { CommunicationsOptionsMenu } from './CommunicationsOptionsMenu';
import { ExpandablePanel, ExpandToggleButton } from './ExpandablePanel';
import { FallbackAvatar } from '../../general/user-profile/components/Avatar';
import { RecipientAvatars } from './RecipientAvatars';
import { GET_SENT_EMAIL } from './sent-email.gql';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { VolunteerCommonProfileEmail } from '../volunteer-common-profile/__generated__/VolunteerCommonProfileEmail';
import { OpenProfileRequest } from './InboxThreadPanel';
import {
  CommunicationLogItem,
  CommunicationsPerson,
  humaniseTag,
  personInitials,
  personName,
  rosterStatus,
  sentStatuses,
} from './communications-model';
import { CommunicationStatusChip } from '../../../common/communications/CommunicationStatusChip';
import { EmailContent } from '../../../common/communications/EmailContent';
import { SmsThread } from '../../../common/communications/SmsThread';

interface SentMessagePanelProps {
  item: CommunicationLogItem;
  sender?: CommunicationsPerson;
  recipients: CommunicationsPerson[];
  onClose: () => void;
  onOpenProfile: (profile: OpenProfileRequest) => void;
}

interface GetSentEmail {
  em: { email: VolunteerCommonProfileEmail | null };
}

interface GetSentEmailVariables {
  emailId: string;
}

export const SentMessagePanel = ({ item, sender, recipients, onClose, onOpenProfile }: SentMessagePanelProps) => {
  const [expanded, setExpanded] = useState(false);
  const isRoster = item.kind === 'roster';
  const recipientCount = isRoster ? 1 : item.sent.recipientCount;
  const preview = isRoster ? item.roster.preview : item.sent.preview;
  const tag = isRoster ? item.roster.activityName : humaniseTag(item.sent.tag);

  const single = recipients.length === 1 && recipientCount === 1 ? recipients[0] : undefined;
  const title = single
    ? personName(single)
    : (!isRoster && recipientCount === 1 && item.sent.recipient) || `${recipientCount} recipients`;

  const channelLabel = isRoster || item.sent.channel === 'sms' ? 'SMS' : 'Email';
  const channelKey: 'sms' | 'email' = isRoster ? 'sms' : item.sent.channel;
  const statuses = isRoster ? [{ status: rosterStatus(item.roster), count: undefined }] : sentStatuses(item.sent);
  const openable = Boolean(single && single.userId);
  const isEmail = !isRoster && item.sent.channel === 'email';

  const { data: emailData, loading: emailLoading } = useCampfireQuery<GetSentEmail, GetSentEmailVariables>(
    GET_SENT_EMAIL,
    {
      options: {
        variables: { emailId: isRoster ? '' : item.sent.groupKey },
        skip: !isEmail,
      },
    }
  );
  const email = emailData?.em.email ?? null;

  return (
    <ExpandablePanel expanded={expanded} onCollapse={() => setExpanded(false)}>
      <Box
        flexShrink={0}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        padding='14px 24px'
        style={{ borderBottom: '1px solid #EFEFEF' }}
      >
        <Box display='flex' alignItems='center' minWidth={0}>
          {single ? (
            <FallbackAvatar size={40} initials={personInitials(single)} style={{ flexShrink: 0 }} />
          ) : (
            <RecipientAvatars people={recipients} totalCount={recipientCount} />
          )}
          <Box paddingLeft='14px' minWidth={0}>
            <Typography
              noWrap
              onClick={() => {
                if (single && single.userId) onOpenProfile({ profileId: single.profileId, userId: single.userId });
              }}
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#444444',
                cursor: openable ? 'pointer' : 'default',
              }}
            >
              {title}
            </Typography>
            <Typography noWrap style={{ fontSize: 12, color: '#646464', marginTop: 2 }}>
              {tag ? `${channelLabel} \u00b7 ${tag}` : channelLabel}
            </Typography>
          </Box>
        </Box>

        <Box display='flex' alignItems='center' style={{ gap: 8, flexShrink: 0 }}>
          <CommunicationsOptionsMenu
            options={[
              {
                label: 'Open profile',
                Icon: Person,
                disabled: !openable,
                onClick: () => {
                  if (single && single.userId) onOpenProfile({ profileId: single.profileId, userId: single.userId });
                },
              },
              {
                label: 'View all communications',
                Icon: Forum,
                disabled: !openable,
                onClick: () => {
                  if (single && single.userId) {
                    onOpenProfile({ profileId: single.profileId, userId: single.userId, openOn: 'communications' });
                  }
                },
              },
            ]}
          />
          <ExpandToggleButton expanded={expanded} onToggle={() => setExpanded((value) => !value)} />
          <IconButton size='small' aria-label='Close message' onClick={onClose}>
            <Close style={{ fontSize: 18, color: '#646464' }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        flexGrow={1}
        padding='22px 24px'
        display='flex'
        flexDirection='column'
        minHeight={0}
        style={{ backgroundColor: '#F9F9F9', overflowY: 'auto', boxSizing: 'border-box' }}
      >
        {isEmail ? (
          <EmailContent
            subject={email?.subject ?? preview}
            from={sender ? personName(sender) : 'System'}
            to={email?.toEmail ?? title}
            sentAt={item.sortAt}
            body={email?.body ?? null}
            loading={emailLoading}
          />
        ) : (
          <SmsThread
            messages={[
              {
                id: isRoster ? item.roster.smsLogId : item.sent.groupKey,
                body: preview,
                inbound: isRoster && item.roster.direction === 'received',
                at: item.sortAt,
                author:
                  isRoster && item.roster.direction === 'received' ? title : sender ? personName(sender) : 'Automated',
              },
            ]}
          />
        )}
      </Box>

      <Box
        flexShrink={0}
        padding='14px 24px 18px 24px'
        style={{ borderTop: '1px solid #EFEFEF', backgroundColor: '#FFFFFF', boxSizing: 'border-box' }}
      >
        <Box display='flex' alignItems='center' flexWrap='wrap' style={{ gap: 8 }}>
          {statuses.map((summary) => (
            <CommunicationStatusChip
              key={summary.status}
              status={summary.status}
              channel={channelKey}
              count={summary.count}
            />
          ))}
          <Typography style={{ fontSize: 13, color: '#646464' }}>
            {`Sent to ${recipientCount} ${recipientCount === 1 ? 'recipient' : 'recipients'}`}
          </Typography>
        </Box>
      </Box>
    </ExpandablePanel>
  );
};
